import React, { useState, useEffect } from 'react';
import { TextField, Button, Typography, Box, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, TextareaAutosize } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import './AddMessage.css';
import { LocalizationProvider, DatePicker } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { format } from 'date-fns';

const addMessage = async (date: Date | null, message: string): Promise<{ message: string, status: number }> => {
    const endpoint = process.env.REACT_APP_API_ENDPOINT;
    const token = localStorage.getItem('token');

    const formattedDate: string = date ? format(date, 'yyyy-MM-dd') : '';

    if (!formattedDate) {
        return { message: 'Invalid date', status: 400 };
    }

    const response = await fetch(`${endpoint}/messages`, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ formattedDate, message })
    });

    if (response.status === 401) {
        return { message: 'Unauthorized', status: 401 };
    }

    if (response.ok) {
        return { message: 'Message added or updated successfully', status: 201 };
    } else {
        return { message: 'Failed to add or update message', status: response.status };
    }
};

interface AddMessageProps {
    onBack: () => void;
}

const AddMessage: React.FC<AddMessageProps> = ({ onBack }) => {
    const [message, setMessage] = useState('');
    const [selectedDate, setSelectedDate] = useState<Date | null>(new Date());
    const [authError, setAuthError] = useState(false);
    const [submitError, setSubmitError] = useState(false);
    const [success, setSuccess] = useState(false);
    const [isSubmitDisabled, setIsSubmitDisabled] = useState(true);
    const navigate = useNavigate();

    useEffect(() => {
        setIsSubmitDisabled(!selectedDate || !message);
    }, [selectedDate, message]);

    const handleDateChange = (date: Date | null) => {
        setSelectedDate(date);
    };

    const handleMessageChange = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
        setMessage(event.target.value);
    };

    const handleSubmit = async (event: React.FormEvent) => {
        event.preventDefault();
        const result = await addMessage(selectedDate, message);
        if (result.status === 401) {
            setAuthError(true);
        } else if (result.status !== 201) {
            setSubmitError(true);
        } else {
            setSuccess(true);
        }
    };

    const handleAuthErrorClose = () => {
        setAuthError(false);
    };

    const handleSubmitErrorClose = () => {
        setSubmitError(false);
    };

    const handleSuccessClose = () => {
        setSuccess(false);
        navigate('/retrieve-message');
    };

    return (
        <Box className="add-message" sx={{ textAlign: 'center', margin: '0 auto', borderRadius: '8px', display: 'flex', flexDirection: 'column', flexGrow: 1 }}>
            <Typography variant="h5" component="h1" color="primary">
                Add Message
            </Typography>
            <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', flexGrow: 1 }}>
                <LocalizationProvider dateAdapter={AdapterDateFns}>
                    <DatePicker
                        label="Select Date"
                        value={selectedDate}
                        onChange={handleDateChange}
                        renderInput={(params) => <TextField {...params} fullWidth margin="normal" />}
                    />
                </LocalizationProvider>
                <Box sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', mt: 2 }}>
                    <TextareaAutosize
                        value={message}
                        onChange={handleMessageChange}
                        placeholder="Enter message"
                        style={{ flexGrow: 1, resize: 'none', padding: '8px', borderRadius: '8px', border: '1px solid #ccc', fontFamily: 'inherit', fontSize: 'inherit' }}
                    />
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
                    <Button type="submit" variant="contained" color="primary" disabled={isSubmitDisabled}>
                        Submit
                    </Button>
                    <Button onClick={() => navigate('/retrieve-message')} variant="contained" color="secondary" sx={{ ml: 2 }}>
                        Back
                    </Button>
                </Box>
            </form>
            <Dialog open={authError} onClose={handleAuthErrorClose}>
                <DialogTitle>Invalid Username or Password</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        The username or password you entered is incorrect. Please try again.
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleAuthErrorClose} color="primary">
                        Close
                    </Button>
                </DialogActions>
            </Dialog>
            <Dialog open={submitError} onClose={handleSubmitErrorClose}>
                <DialogTitle>Submission Error</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        There was an error submitting your message. Please try again.
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleSubmitErrorClose} color="primary">
                        Close
                    </Button>
                </DialogActions>
            </Dialog>
            <Dialog open={success} onClose={handleSuccessClose}>
                <DialogTitle>Success</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        Your message has been successfully added.
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleSuccessClose} color="primary">
                        Close
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default AddMessage;